import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const PAGE_SIZE = 10;

const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2, style = {} }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={style}>
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    plus: 'M12 5v14 M5 12h14',
    upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M17 8l-5-5-5 5 M12 3v12',
    search: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
    eye: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
    trash: 'M3 6h18 M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2',
    x: 'M18 6L6 18 M6 6l12 12',
    edit: 'M11 4H4a2 2 0 00-2 2v14a2 2 0 00 2 2h14a2 2 0 00 2-2v-7 M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z',
    check: 'M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4L12 14.01l-3-3'
};

export default function OperatorClientDocuments({ user, operatorDetails }) {
    const [docs, setDocs] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [clientFilter, setClientFilter] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [toast, setToast] = useState(null);

    // Form state
    const [clientId, setClientId] = useState('');
    const [documentType, setDocumentType] = useState('');
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const fetchDocs = async () => {
        setLoading(true);
        try {
            // Fetch both client documents and vendor documents for all clients
            const [clientDocsRes, vendorDocsRes] = await Promise.all([
                axios.get(`/api/client-documents/${user?.operatorId || 'all'}`),
                axios.get(`/api/vendor-documents/${user?.operatorId || 'all'}`)
            ]);

            // Combine and format both types of documents
            const allDocs = [
                ...clientDocsRes.data.map(d => ({ ...d, source: 'client' })),
                ...vendorDocsRes.data.map(d => ({
                    ...d,
                    clientId: d.vendorId, // Map vendorId to clientId for consistency
                    documentType: d.documentType,
                    fileName: d.fileName,
                    filePath: d.filePath,
                    status: d.status,
                    createdAt: d.createdAt,
                    source: 'vendor'
                }))
            ];

            setDocs(allDocs);
        } catch (err) {
            console.error('Fetch docs error:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchClients = async () => {
        try {
            const res = await axios.get(`/api/clients/${user?.operatorId || 'all'}`);
            setClients(res.data);
        } catch (err) {
            console.error('Fetch clients error:', err);
        }
    };

    useEffect(() => {
        fetchDocs();
        fetchClients();
    }, [user]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return docs.filter(d => {
            const clientMatch = !clientFilter || (d.clientId?._id || d.clientId) === clientFilter;
            const searchMatch = !q ||
                (d.clientId?.companyName || '').toLowerCase().includes(q) ||
                d.documentType.toLowerCase().includes(q) ||
                d.fileName.toLowerCase().includes(q);
            return clientMatch && searchMatch;
        });
    }, [docs, search, clientFilter]);

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!file) return showToast('Please select a file', 'error');

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('clientId', clientId);
            formData.append('documentType', documentType);
            formData.append('operatorId', user?.operatorId || user?._id);
            formData.append('uploadedBy', 'operator');

            await axios.post('/api/client-documents', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            showToast('Document uploaded successfully');
            fetchDocs();
            closeModal();
        } catch (err) {
            console.error('Upload error:', err.response?.data || err);
            const msg = err.response?.data?.message || 'Failed to upload document';
            showToast(msg, 'error');
        } finally {
            setUploading(false);
        }
    };

    const handleUpdateStatus = async (docId, newStatus) => {
        try {
            await axios.put(`/api/client-documents/${docId}`, { status: newStatus });
            showToast('Document status updated');
            fetchDocs();
        } catch (err) {
            showToast('Failed to update status', 'error');
        }
    };

    const handleDelete = async (doc) => {
        if (!window.confirm('Delete this document?')) return;
        try {
            await axios.delete(`/api/client-documents/${doc._id}`);
            showToast('Document deleted');
            fetchDocs();
        } catch (err) {
            showToast('Failed to delete', 'error');
        }
    };

    const openModal = () => {
        setClientId('');
        setDocumentType('');
        setFile(null);
        setShowModal(true);
    };

    const closeModal = () => setShowModal(false);

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
    };

    return (
        <div className="docs-page">
            <style>{`
                .docs-page { padding: 30px; background: #f8fafc; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .header-section { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
                .title-wrap h1 { font-size: 28px; font-weight: 800; color: #1e293b; margin: 0; }

                .upload-btn {
                    background: #00B2A9; color: white; border: none; padding: 10px 20px;
                    border-radius: 8px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 8px;
                    transition: all 0.2s; box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
                }
                .upload-btn:hover { background: #16539C; transform: translateY(-1px); }

                .filter-bar {
                    display: flex; align-items: center; gap: 14px; margin-bottom: 20px;
                    padding: 14px 20px; background: white; border-radius: 12px;
                    border: 1px solid #e2e8f0; box-shadow: 0 1px 4px rgba(0,0,0,0.03);
                }
                .filter-bar label {
                    font-size: 13px; font-weight: 600; color: #475569; white-space: nowrap;
                }
                .filter-select {
                    padding: 9px 14px; border: 1.5px solid #e2e8f0; border-radius: 8px;
                    font-size: 14px; color: #1e293b; background: #f8fafc; outline: none;
                    min-width: 200px; cursor: pointer; transition: all 0.2s;
                    font-family: 'Inter', sans-serif;
                }
                .filter-select:focus { border-color: #00B2A9; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1); }

                .search-input {
                    padding: 9px 14px; border: 1.5px solid #e2e8f0; border-radius: 8px;
                    font-size: 14px; color: #1e293b; background: #f8fafc; outline: none;
                    min-width: 250px; transition: all 0.2s;
                    font-family: 'Inter', sans-serif;
                }
                .search-input:focus { border-color: #00B2A9; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1); }

                .content-box {
                    background: white; border-radius: 16px; border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.03); padding: 24px;
                }

                .table-container { overflow-x: auto; }
                .docs-table { width: 100%; border-collapse: collapse; }
                .docs-table th {
                    padding: 14px 20px; text-align: left; font-size: 12px; font-weight: 600;
                    color: #64748b; text-transform: uppercase; border-bottom: 1px solid #e2e8f0; background: #fff;
                }
                .docs-table td { padding: 16px 20px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
                .docs-table tr:hover { background: #f8fafc; }

                .client-text { font-weight: 600; color: #1e293b; }
                .doc-type { color: #334155; }

                .status-badge {
                    padding: 4px 12px; border-radius: 99px; font-size: 12px; font-weight: 600;
                }
                .status-valid { background: #dcfce7; color: #166534; }
                .status-uploaded { background: #fef9c3; color: #92400e; }
                .status-under-review { background: #fef9c3; color: #92400e; }
                .status-rejected { background: #fee2e2; color: #991b1b; }

                .act-btn {
                    border: none; background: none; padding: 6px; cursor: pointer; border-radius: 6px;
                    color: #94a3b8; transition: all 0.2s;
                }
                .act-btn:hover { background: #f1f5f9; color: #00B2A9; }

                /* Modal */
                .modal-overlay {
                    position: fixed; inset: 0; background: rgba(15, 23, 42, 0.4);
                    backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 1000;
                }
                .modal-content {
                    background: white; border-radius: 20px; width: 100%; max-width: 480px;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                }
                .modal-header { padding: 20px 24px; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; }
                .modal-header h2 { font-size: 18px; font-weight: 700; color: #1e293b; }
                .modal-body { padding: 24px; }
                .form-group { display: flex; flex-direction: column; gap: 6px; margin-bottom: 16px; }
                .form-label { font-size: 13px; font-weight: 600; color: #475569; }
                .form-input { padding: 10px 12px; border: 1px solid #e2e8f0; border-radius: 10px; font-size: 14px; outline: none; transition: all 0.2s; }
                .form-input:focus { border-color: #00B2A9; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1); }

                .form-select { padding: 10px 12px; border: 1px solid #e2e8f0; border-radius: 10px; font-size: 14px; outline: none; transition: all 0.2s; background: white; }
                .form-select:focus { border-color: #00B2A9; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1); }

                .file-label {
                    border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px; cursor: pointer; display: flex; align-items: center; gap: 10px; font-size: 14px; color: #64748b;
                }

                .modal-footer { padding: 20px 24px; border-top: 1px solid #f1f5f9; display: flex; justify-content: flex-end; gap: 12px; }
                .cancel-btn { padding: 10px 20px; border: 1px solid #e2e8f0; background: white; border-radius: 8px; font-size: 14px; font-weight: 600; color: #64748b; cursor: pointer; }
                .submit-btn { padding: 10px 24px; border: none; background: #00B2A9; color: white; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; }

                .toast { position: fixed; top: 24px; right: 24px; padding: 14px 24px; border-radius: 10px; background: #1e293b; color: white; z-index: 2000; }
                .toast.success { background: #10b981; }
                .toast.error { background: #ef4444; }
            `}</style>

            {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}

            <div className="header-section">
                <div className="title-wrap">
                    <h1>Client Documents</h1>
                    <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#64748b' }}>
                        Manage documents uploaded by clients
                    </p>
                </div>
                <button className="upload-btn" onClick={openModal}>
                    <Icon d={icons.upload} size={18} />
                    Upload Document
                </button>
            </div>

            <div className="filter-bar">
                <label>Filter by Client:</label>
                <select
                    className="filter-select"
                    value={clientFilter}
                    onChange={e => setClientFilter(e.target.value)}
                >
                    <option value="">All Clients</option>
                    {clients.map(c => (
                        <option key={c._id} value={c._id}>{c.companyName}</option>
                    ))}
                </select>
                <label>Search:</label>
                <input
                    type="text"
                    className="search-input"
                    placeholder="Search documents..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
            </div>

            <div className="content-box">
                <div className="table-container">
                    <table className="docs-table">
                        <thead>
                            <tr>
                                <th>Client</th>
                                <th>Document Type</th>
                                <th>File Name</th>
                                <th>Status</th>
                                <th>Upload Date</th>
                                <th>Source</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="8" style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>Loading documents...</td></tr>
                            ) : filtered.length === 0 ? (
                                <tr><td colSpan="8" style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>No documents found.</td></tr>
                            ) : (
                                filtered.map(doc => (
                                    <tr key={doc._id}>
                                        <td><div className="client-text">{doc.clientId?.companyName || 'Deleted Client'}</div></td>
                                        <td><div className="doc-type">{doc.documentType}</div></td>
                                        <td style={{ color: '#1e293b', fontWeight: '500' }}>{doc.fileName}</td>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <span className={`status-badge status-${doc.status?.toLowerCase().replace(' ', '-') || 'uploaded'}`}>{doc.status || 'Uploaded'}</span>
                                                {doc.status === 'Uploaded' && (
                                                    <>
                                                        <button
                                                            className="act-btn"
                                                            style={{ color: '#10b981', background: '#dcfce7', fontSize: 11, fontWeight: 'bold' }}
                                                            onClick={() => handleUpdateStatus(doc._id, 'Valid')}
                                                        >
                                                            Approve
                                                        </button>
                                                        <button
                                                            className="act-btn"
                                                            style={{ color: '#f59e0b', background: '#fef9c3', fontSize: 11, fontWeight: 'bold' }}
                                                            onClick={() => handleUpdateStatus(doc._id, 'Under Review')}
                                                        >
                                                            Review
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ color: '#64748b' }}>{formatDate(doc.createdAt)}</td>
                                        <td style={{ color: '#64748b', textTransform: 'capitalize' }}>{doc.source === 'vendor' ? 'Operator' : (doc.uploadedBy || 'Client')}</td>
                                        <td>
                                            <div style={{ display: 'flex', gap: 8 }}>
                                                <button
                                                    className="act-btn"
                                                    title="View"
                                                    onClick={() => window.open(`/api/uploads/${doc.filePath}`, '_blank')}
                                                >
                                                    <Icon d={icons.eye} size={16} />
                                                </button>
                                                <button className="act-btn" style={{ color: '#ef4444' }} title="Delete" onClick={() => handleDelete(doc)}>
                                                    <Icon d={icons.trash} size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {showModal && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Upload Client Document</h2>
                            <button className="act-btn" onClick={closeModal}><Icon d={icons.x} size={20} /></button>
                        </div>
                        <form onSubmit={handleUpload}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label className="form-label">Client</label>
                                    <select
                                        className="form-select"
                                        required
                                        value={clientId}
                                        onChange={e => setClientId(e.target.value)}
                                    >
                                        <option value="">Select a client</option>
                                        {clients.map(c => <option key={c._id} value={c._id}>{c.companyName}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Document Type</label>
                                    <input
                                        className="form-input"
                                        required
                                        placeholder="e.g. Business License"
                                        value={documentType}
                                        onChange={e => setDocumentType(e.target.value)}
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Select File</label>
                                    <input
                                        type="file"
                                        id="docFile"
                                        style={{ display: 'none' }}
                                        onChange={e => setFile(e.target.files[0])}
                                    />
                                    <label htmlFor="docFile" className="file-label">
                                        <Icon d={icons.upload} size={16} />
                                        {file ? file.name : 'Choose file - No file chosen'}
                                    </label>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="cancel-btn" onClick={closeModal}>Cancel</button>
                                <button type="submit" className="submit-btn" disabled={uploading}>
                                    {uploading ? 'Uploading...' : 'Upload'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}